import React from 'react';
import {
  withSystem,
  Plugins,
  Expressions,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  ExpressionEditor,
  updateExpressionAction,
  extendExpressionEditorActionAtPath,
  useExpressionEditorContext,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TText,
  TRecord,
  EEnum,
  TEnum,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  Button,
  CollapseIcon,
  ExpandVerticalIcon,
  View,
} from '@packages/systems/spring';

import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

import {
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {stripAnn} from '@packages/utilities/annotated';
import {
  MENU_STATE_HIDE,
  MENU_STATE_SHOW,
  TEMP_DROPDOWN_MENU_PATH,
} from '@packages/utilities/expression-path-constants';

const {getIn, getEnum} = ContextualExpressions;

const CHILDREN_PATH = [{in: 'Record', at: 'children'}] satisfies ExpressionPath;
const DELAY_PATH = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'data-delay'},
] satisfies ExpressionPath;
const HOVER_PATH = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'data-hover'},
] satisfies ExpressionPath;

const Separator = emotionStyled('div')({
  width: '100%',
  height: tokens.space[2],
});

const DropdownWrapperDataType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields: {
    tag: withSystem(
      {
        private: true,
        defaultValue: 'div',
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('TagsDropdown')))
    ),
    temp: TRecord({
      dropdown: TRecord({
        menu: withSystem(
          {
            label: 'Menu',
            defaultValue: MENU_STATE_HIDE,
          },
          TEnum({show: 'Show', hide: 'Hide'})
        ),
      }),
    }),
    children: withSystem(
      {
        label: 'Add Link',
        labelVariant: 'hidden',
      },
      TTypeConstructor(
        qualify(Plugins.name('Dropdown'), typeName('AddDropdownLink'))
      )
    ),
    attr: TRecord({
      'data-delay': withSystem(
        {
          label: 'Close Delay',
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('MSNumber')))
      ),
      'data-hover': withSystem(
        {
          labelVariant: 'hidden',
          label: 'Open menu on hover',
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
        )
      ),
    }),
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    dropdown: TRecord({
      type: withSystem(
        {
          private: true,
          defaultValue: 'wrapper',
        },
        TText
      ),
    }),
  },

  render: function DropdownWrapper({expr, dispatch}) {
    const eeContext = useExpressionEditorContext();
    const [menu, setMenu] = React.useState(
      getIn(expr, TEMP_DROPDOWN_MENU_PATH)
    );
    const [children, setChildren] = React.useState(getIn(expr, CHILDREN_PATH));
    const [delay, setDelay] = React.useState(getIn(expr, DELAY_PATH));
    const [hover, setHover] = React.useState(getIn(expr, HOVER_PATH));

    React.useEffect(() => {
      const nextMenu = getIn(expr, TEMP_DROPDOWN_MENU_PATH);
      if (nextMenu !== menu) {
        setMenu(nextMenu);
      }
    }, [expr, menu]);

    React.useEffect(() => {
      const nextChildren = getIn(expr, CHILDREN_PATH);
      if (nextChildren !== children) {
        setChildren(nextChildren);
      }
    }, [expr, children]);
    React.useEffect(() => {
      const nextDelay = getIn(expr, DELAY_PATH);
      if (nextDelay !== delay) {
        setDelay(nextDelay);
      }
    }, [expr, delay]);
    React.useEffect(() => {
      const nextHover = getIn(expr, HOVER_PATH);
      if (nextHover !== hover) {
        setHover(nextHover);
      }
    }, [expr, hover]);

    if (
      menu === null ||
      menu === undefined ||
      children === null ||
      children === undefined ||
      delay === null ||
      delay === undefined ||
      hover === null ||
      hover === undefined
    ) {
      return null;
    }

    const onChangeAt = (
      at: Array<{
        at: string;
        in: 'Record';
      }>
    ) => {
      return extendExpressionEditorActionAtPath(dispatch)(at);
    };

    const onChangeChildren = (childrenExpr: any) => {
      const newExpr = Expressions.setIn(
        Expressions.setIn(
          stripAll(expr),
          CHILDREN_PATH,
          childrenExpr.payload.newExpression
        ),
        TEMP_DROPDOWN_MENU_PATH,
        EEnum(MENU_STATE_SHOW)
      );
      dispatch(updateExpressionAction(newExpr));
    };

    if (eeContext === 'compact') {
      const menuValue = getEnum(stripAnn(menu));
      const isMenuOpen = menuValue === MENU_STATE_SHOW;
      const icon = isMenuOpen ? CollapseIcon : ExpandVerticalIcon;
      const label = isMenuOpen ? 'Hide menu' : 'Show menu';
      const toggleMenu = () => {
        const nextMenuValue = isMenuOpen ? MENU_STATE_HIDE : MENU_STATE_SHOW;
        const newExpr = Expressions.setIn(
          stripAll(expr),
          TEMP_DROPDOWN_MENU_PATH,
          EEnum(nextMenuValue)
        );
        dispatch(updateExpressionAction(newExpr));
      };

      return (
        <View padding="$1">
          <Button
            onClick={toggleMenu}
            prefix={icon}
            variant="ghost"
            density="compact"
          >
            {label}
          </Button>
        </View>
      );
    }

    return (
      <>
        <ExpressionEditor
          expression={menu}
          dispatch={onChangeAt(TEMP_DROPDOWN_MENU_PATH)}
        />
        <Separator />
        <ExpressionEditor
          expression={delay}
          dispatch={onChangeAt(DELAY_PATH)}
        />
        <Separator />
        <ExpressionEditor
          expression={hover}
          dispatch={onChangeAt(HOVER_PATH)}
        />
        <Separator />
        <ExpressionEditor expression={children} dispatch={onChangeChildren} />
      </>
    );
  },
});

export default DropdownWrapperDataType;
